<template>
    <div
        class="relative"
        data-test="select-container"
        ref="wrapper"
        v-clickoutside="close"
    >
        <Popover
            :class-name="popoverClassName"
            data-test="select-popover"
            event="manual"
            placement="bottom-start"
            :same-width="sameWidth"
            ref="popper"
            @enter="onOpen"
            @keydown.down.prevent.stop="onKeyDown"
            @keydown.enter.prevent.stop="onKeyEnter"
            @keydown.esc.stop.prevent="onKeyEscape"
            @keydown.tab.stop.prevent="onKeyTab"
        >
            <template #trigger>
                <div
                    data-test="select-trigger-container"
                    ref="trigger"
                    @click="toggle"
                >
                    <slot
                        :bold-highlighted="boldHighlighted"
                        :disabled="disabled"
                        :highlighted="highlighted"
                        :invisible="invisible"
                        name="trigger"
                        :opened="show"
                        :padding-class-name="triggerPaddingClassName"
                        :placeholder="placeholder"
                        :selected="multiple ? selectedOptions : selectedOptions[0] || null"
                    >
                        <SelectTrigger
                            :bold-highlighted="boldHighlighted"
                            :disabled="disabled"
                            :highlighted="highlighted"
                            :invisible="invisible"
                            :opened="show"
                            :padding-class-name="triggerPaddingClassName"
                        >
                            <template #triggerIcon>
                                <slot name="triggerIcon" />
                            </template>
                        </SelectTrigger>
                    </slot>
                </div>
            </template>

            <template v-if="filterable">
                <SelectSearch
                    class="border-b border-gray-200"
                    data-test="select-search"
                    ref="search"
                    v-model="search"
                />

                <div
                    class="italic py-2 text-center text-gray-600"
                    data-test="select-no-results"
                    v-text="messages.noResults"
                    v-if="!filteredOptions.length"
                />
            </template>
            <template v-else-if="!internalOptions.length">
                <div
                    class="italic py-2 text-center text-gray-600"
                    data-test="select-no-options"
                    v-text="messages.noOption"
                />
            </template>

            <div v-if="initiallyOpened">
                <SelectOptions
                    data-test="select-options-container"
                    ref="optionList"
                    v-show="show && filteredOptions.length"
                >
                    <template v-if="hasSelectAll">
                        <SelectAll
                            :all-selected="isAllSelected"
                            class="border-b border-gray-200"
                            data-test="select-all"
                            :partially-selected="isPartiallySelected"
                        />
                    </template>
                    <slot :options="filteredOptions">
                        <template v-if="isGrouped">
                            <SelectOptionGroup
                                data-test="select-option-group"
                                :label="group.label"
                                :key="index"
                                v-for="(group, index) in filteredOptions"
                            >
                                <SelectOption
                                    data-test="select-group-option"
                                    :disabled="option[disabledKey]"
                                    :label="option[labelKey]"
                                    :tooltip="option.tooltip"
                                    :value="option[valueKey]"
                                    :key="option[valueKey]"
                                    ref="options"
                                    v-for="option in group.options"
                                />
                            </SelectOptionGroup>
                        </template>
                        <template v-else>
                            <SelectOption
                                :disabled="option[disabledKey]"
                                :label="option[labelKey]"
                                :tooltip="option.tooltip"
                                :value="option[valueKey]"
                                :key="option[valueKey]"
                                ref="options"
                                v-for="option in filteredOptions"
                            />
                        </template>
                    </slot>
                </SelectOptions>
            </div>
        </Popover>
    </div>
</template>

<script>
    import { t } from '../../locale/index.js';
    import SelectOption from './SelectOption.vue';
    import SelectOptions from './SelectOptions.vue';
    import Popover from '../Popover/Popover.vue';
    import SelectTrigger from './SelectTrigger.vue';
    import SelectOptionGroup from './SelectOptionGroup.vue';
    import SelectSearch from './SelectSearch.vue';
    import SelectAll from './SelectAll.vue';

    const selectAllValue = 'selectAll';

    /**
     * Presents a list of options and allows a user to select one or many of the options.
     * @displayName Select
     */
    export default {
        name: 'ActivixSelect',

        components: {
            SelectAll,
            SelectSearch,
            SelectOptionGroup,
            SelectTrigger,
            Popover,
            SelectOption,
            SelectOptions,
        },

        provide() {
            return {
                state: this.provide.state,
                events: this.provide.events,
                refs: this.provide.refs,
            };
        },

        props: {
            color: {
                default: 'white',
                type: String,
                validator: value => ['white', 'gray', 'transparent'].includes(value),
            },
            disabled: {
                default: false,
                type: Boolean,
            },
            disabledKey: {
                default: 'disabled',
                type: String,
            },
            filterable: {
                default: false,
                type: Boolean,
            },
            filterMethod: {
                type: Function,
            },
            focusOnEnter: {
                default: true,
                type: Boolean,
            },
            heightClassName: {
                default: 'max-h-60',
                type: String,
            },
            highlighted: {
                default: false,
                type: Boolean,
            },
            boldHighlighted: {
                default: false,
                type: Boolean,
            },
            invisible: {
                default: false,
                type: Boolean,
            },
            labelKey: {
                default: 'label',
                type: String,
            },
            multiple: {
                default: false,
                type: Boolean,
            },
            noOptionMessage: {
                default: '',
                type: String,
            },
            noResultsMessage: {
                default: '',
                type: String,
            },
            nullable: {
                default: false,
                type: Boolean,
            },
            nullableOptionLabel: {
                default: '---',
                type: String,
            },
            options: {
                required: true,
                type: Array,
            },
            placeholder: {
                default: '---',
                type: String,
            },
            sameWidth: {
                default: true,
                type: Boolean,
            },
            selectAll: {
                default: false,
                type: Boolean,
            },
            taggable: {
                default: false,
                type: Boolean,
            },
            triggerPaddingClassName: {
                default: 'py-2 px-3',
                type: null,
            },
            value: {
                required: true,
                type: null,
            },
            valueKey: {
                default: 'value',
                type: String,
            },
            widthClassName: {
                default: 'min-w-48',
                type: String,
            },
            withCount: {
                default: true,
                type: Boolean,
            },
        },

        data() {
            return {
                show: false,
                initiallyOpened: false, // Changes to true once user opens the dropdown. Stays true until the component is unmounted.
                search: '',
                provide: {
                    state: {
                        selectedOptions: [],
                        isGrouped: false,
                        isTaggable: this.multiple && this.taggable,
                        multiple: this.multiple,
                        placeholder: this.placeholder,
                        selectAllValue,
                        withCount: this.withCount,
                    },
                    events: {
                        onSelect: this.onSelect,
                        onDelete: this.onDelete,
                    },
                    refs: {
                        trigger: this.$refs.trigger,
                    },
                },
            };
        },

        computed: {
            formattedValue() {
                return this.multiple ? this.value : this.value || null;
            },

            internalOptions() {
                const options = [...this.options];
                const emptyOptionExists = options.some(option => {
                    return option[this.valueKey] === null || option[this.valueKey] === '';
                });

                if (this.nullable && !this.multiple && !emptyOptionExists && !this.isGrouped) {
                    options.unshift({
                        [this.labelKey]: this.nullableOptionLabel,
                        [this.valueKey]: null,
                    });
                }

                return options;
            },

            flattenedOptions() {
                if (!this.isGrouped) {
                    return this.internalOptions;
                }

                return this.options.reduce((options, group) => {
                    return options.concat(group.options);
                }, []);
            },

            filteredOptions() {
                const method = this.filterMethod ? this.filterMethod : this.internalFilterMethod;

                if (!this.isGrouped) {
                    return this.internalOptions.filter(option => method(option, this.search));
                }

                return this.internalOptions.map(option => {
                    return {
                        ...option,
                        options: option.options.filter(o => method(o, this.search)),
                    };
                });
            },

            selectedOptions() {
                if (this.multiple) {
                    return this.flattenedOptions.filter(option => this.formattedValue.includes(option[this.valueKey]));
                }

                return this.flattenedOptions.filter(option => option[this.valueKey] === this.formattedValue);
            },

            messages() {
                return {
                    noResults: this.noResultsMessage || t('select.noResults'),
                    noOption: this.noOptionMessage || t('select.noOption'),
                };
            },

            popoverClassName() {
                const className = [
                    this.heightClassName,
                    this.widthClassName,
                    'text-base bg-white rounded-md shadow-lg border border-gray-200 overflow-auto',
                ];

                if (!this.$ui.oldFontScale) {
                    className.push('sm:text-sm');
                }

                return className.join(' ');
            },

            allValues() {
                return this.flattenedOptions.map(option => option[this.valueKey]);
            },

            selectedValues() {
                return this.selectedOptions.map(option => option[this.valueKey]);
            },

            isAllSelected() {
                return this.allValues.every(value => this.selectedValues.includes(value));
            },

            isPartiallySelected() {
                return this.allValues.some(value => this.selectedValues.includes(value));
            },

            isGrouped() {
                return this.options.length && this.options.every(option => Array.isArray(option.options));
            },

            hasSelectAll() {
                return this.multiple && this.selectAll;
            },

            popperRef() {
                return this.$refs.popper;
            },
        },

        watch: {
            selectedOptions: {
                immediate: true,
                handler(selectedOptions) {
                    this.provide.state.selectedOptions = selectedOptions.map(selectedOption => ({
                        label: selectedOption[this.labelKey],
                        value: selectedOption[this.valueKey],
                    }));
                },
            },

            isGrouped: {
                immediate: true,
                handler(isGrouped) {
                    this.provide.state.isGrouped = isGrouped;
                },
            },

        },

        methods: {
            clearSearch() {
                if (!this.$refs.search || !this.search.length) {
                    return;
                }

                // We add timeout for the animation on close to not see grow the list when close event is emitted
                setTimeout(() => {
                    this.search = '';
                }, 200);
            },

            onSelect(value, element, isEnter) {
                this.$refs.optionList.focusElement(element);

                if (this.multiple) {
                    if (value === selectAllValue) {
                        if (this.isAllSelected) {
                            this.$emit('input', []);
                        } else {
                            this.$emit('input', this.allValues);
                        }

                        return;
                    }

                    let currentValues = [...(this.formattedValue || [])];

                    if (currentValues.includes(value)) {
                        currentValues = currentValues.filter(currentValue => currentValue !== value);
                    } else {
                        currentValues.push(value);
                    }

                    this.$emit('input', currentValues);
                } else {
                    this.$emit('input', value);
                    this.clearSearch();
                    this.close();

                    if (this.focusOnEnter || !isEnter) {
                        this.focusTrigger();
                    }
                }
            },

            onDelete(value) {
                if (!this.multiple) {
                    return;
                }

                const currentValues = [...(this.formattedValue || [])];

                this.$emit('input', currentValues.filter(currentValue => currentValue !== value));
            },

            onKeyTab() {
                if (!this.$refs.search) {
                    return;
                }

                if (this.$refs.search.isFocused()) {
                    this.$refs.optionList.focus();
                } else {
                    this.$refs.search.focus();
                }
            },

            onKeyDown() {
                if (!this.$refs.search) {
                    return;
                }

                if (this.$refs.search.isFocused()) {
                    this.$refs.optionList.focus();
                }
            },

            onKeyEnter(event) {
                if (!this.$refs.search) {
                    return;
                }

                if (this.$refs.search.isFocused()) {
                    const availableOptions = this.$refs.options.filter(option => !option.disabled);
                    let optionToSelect = availableOptions.find(option => option.isSelected && option.isFocused);

                    if (!optionToSelect && !!this.filteredOptions.length) {
                        optionToSelect = availableOptions.find(option => option.value === this.filteredOptions[0].value);
                    }

                    optionToSelect?.onSelect(event);
                }
            },

            onKeyEscape() {
                this.clearSearch();
                this.close();
                this.focusTrigger();
            },

            onOpen() {
                if (this.$refs.search) {
                    this.$refs.search.focus();
                } else if (this.$refs.optionList) {
                    this.$refs.optionList.focus();
                }
            },
            focusTrigger() {
                const button = this.$refs.trigger.querySelector('button');

                if (!button) {
                    // eslint-disable-next-line no-console
                    console.warn('[ActivixSelect] The "trigger" slot must contain a <button> element.');
                    return;
                }

                button.focus();
            },

            blur() {
                if (this.$refs.wrapper.contains(document.activeElement)) {
                    document.activeElement.blur();
                }
            },

            open() {
                this.show = true;
                this.initiallyOpened = true;
                this.popperRef.open();
                this.$emit('open');
            },

            toggle() {
                if (this.disabled) {
                    return;
                }

                this.show ? this.close() : this.open();
            },

            close() {
                this.show = false;
                this.clearSearch();
                this.$emit('blur');
                this.popperRef.close();
                this.$emit('close');
            },

            internalFilterMethod(option, search) {
                if (!search) {
                    return true;
                }

                return option[this.labelKey].toLowerCase().includes(search.toLowerCase());
            },
        },

        mounted() {
            this.provide.refs.trigger = this.$refs.trigger;
        },
    };
</script>
